import { bindLeaf, findLeaf, forgetLeaf } from './leaf-dom'
import type { Block } from '@w/content-blocks'
import { plain } from '../core/tree-read'
import { blockEditorTheme } from '../theme'
import { useLayoutEffect, type RefObject } from 'react'

/** Publish editable content before sibling overlays measure their ranges. */
export function DirectCommit({ renderer, root }: { renderer: DirectBlocks; root: RefObject<HTMLElement | null> }) {
  useLayoutEffect(() => { renderer.commit(root.current) })
  return null
}

/** Complex inline views keep their existing React context and event owners. */
export function directTextBlock(block: Block): boolean {
  return (block.type === 'p' || block.type === 'h') && block.children.every(child =>
    child.type === 'text' || child.type === 'chars' || child.type === 'br')
}

const marks = [[1, 'strong', 'bold'], [2, 'em', 'italic'], [8, 'u', 'underline'],
  [4, 's', 'strikethrough'], [16, 'code', 'code']] as const

function leaf(block: Block, end: boolean): HTMLElement | Text {
  if (block.type === 'br') {
    const element = document.createElement('br')
    element.dataset.blockId = block.id
    return element
  }
  const text = plain(block)
  const format = block.type === 'chars' ? block.format ?? 0 : 0
  if (!format) return document.createTextNode(text)
  let element: HTMLElement | undefined
  let inner: HTMLElement | undefined
  for (const [bit, tag, style] of marks) {
    if (!(format & bit)) continue
    const wrapper = document.createElement(tag)
    wrapper.className = blockEditorTheme.text[style]
    if (element) wrapper.append(element)
    else inner = wrapper
    element = wrapper
  }
  element ??= document.createElement('span')
  const content = inner ?? element
  text.split('\n').forEach((line, index) => {
    if (index) content.append(document.createElement('br'))
    if (line) content.append(document.createTextNode(line))
  })
  element.dataset.blockId = block.id
  if (end && text.endsWith('\n')) element.append(document.createElement('br'))
  return element
}

/** Keep native Range endpoints alive when a leaf changes its text. */
function patch(old: Element | Text, next: Element | Text): Element | Text {
  if (old instanceof Text && next instanceof Text) {
    if (old.data !== next.data) old.data = next.data
    return old
  }
  if (!(old instanceof Element) || !(next instanceof Element) || old.tagName !== next.tagName) return next
  for (const attribute of Array.from(old.attributes)) {
    if (!next.hasAttribute(attribute.name)) old.removeAttribute(attribute.name)
  }
  for (const attribute of Array.from(next.attributes)) {
    if (old.getAttribute(attribute.name) !== attribute.value) old.setAttribute(attribute.name, attribute.value)
  }
  const wanted = Array.from(next.childNodes)
  let cursor = old.firstChild
  for (const child of wanted) {
    if (cursor?.nodeType === 3 && child.nodeType === 3) {
      if (cursor.nodeValue !== child.nodeValue) cursor.nodeValue = child.nodeValue
      cursor = cursor.nextSibling
    } else if (cursor instanceof Element && child instanceof Element && cursor.tagName === child.tagName) {
      patch(cursor, child)
      cursor = cursor.nextSibling
    } else {
      old.insertBefore(child, cursor)
    }
  }
  while (cursor) {
    const nextChild = cursor.nextSibling
    cursor.remove()
    cursor = nextChild
  }
  return old
}

/** Owns prose block shells and editable descendants; React owns the other blocks as islands. */
export class DirectBlocks {
  private mounted = new Map<string, { block: Block; element: HTMLElement; token: string; mode: string; listed: boolean }>()
  private committed: DirectBlocks['plan'] | null = null
  private committedRoot: HTMLElement | null = null
  private shells = new Map<string, HTMLElement>()
  plan: { block: Block; direct: boolean; token: string; mode: string; listed: boolean }[] = []

  prepare(plan: DirectBlocks['plan']) { this.plan = plan }

  commit(root: HTMLElement | null) {
    if (!root || this.committed === this.plan && this.committedRoot === root) return
    const initial = this.mounted.size === 0 && this.plan.every(item => item.direct)
    const fragment = initial ? document.createDocumentFragment() : null
    const target = fragment ?? root
    const live = new Set(this.plan.filter(item => item.direct).map(item => item.block.id))
    for (const [id, entry] of this.mounted) {
      if (!live.has(id)) { entry.element.remove(); this.mounted.delete(id);
        for (const child of (entry.block.type === 'p' || entry.block.type === 'h' ? entry.block.children : [])) forgetLeaf(root, child.id, entry.element) }
    }
    const islands = this.plan.some(item => !item.direct) ? new Map(Array.from(root.querySelectorAll<HTMLElement>('[data-block-id]'), node => [node.dataset.blockId, node])) : null
    const end = root.querySelector(':scope > [data-lexical-cursor]')
    let anchor: ChildNode | null = fragment ? null : end
    for (const item of this.plan.toReversed()) {
      const { block, token, mode, listed } = item
      if (!item.direct) {
        let island: HTMLElement | null = islands?.get(block.id) ?? null
        while (island?.parentElement && island.parentElement !== root) island = island.parentElement
        if (island?.parentElement === root) anchor = island
        continue
      }
      if (block.type !== 'p' && block.type !== 'h') continue
      let entry = this.mounted.get(block.id)
      const unchanged = entry?.block === block && entry.token === token && entry.mode === mode && entry.listed === listed
      if (unchanged && entry) {
        if (entry.element.parentElement !== root || entry.element.nextSibling !== anchor) target.insertBefore(entry.element, anchor)
        anchor = entry.element
        continue
      }
      const tag = block.type === 'p' ? 'p' : `h${block.level}`
      if (entry && (entry.token !== token || entry.element.tagName.toLowerCase() !== tag)) {
        entry.element.remove()
        if (entry.block.type === 'p' || entry.block.type === 'h') {
          for (const child of entry.block.children) forgetLeaf(root, child.id, entry.element)
        }
        entry = undefined
      }
      if (!entry) {
        let shell = this.shells.get(tag)
        if (!shell) {
          shell = document.createElement(tag)
          shell.className = block.type === 'p' ? blockEditorTheme.paragraph : blockEditorTheme.heading[`h${block.level}`]
          this.shells.set(tag, shell)
        }
        entry = { block, token, mode, listed, element: shell.cloneNode(false) as HTMLElement }
        entry.element.dataset.blockId = block.id
        this.mounted.set(block.id, entry)
        this.write(root, entry.element, block, null)
      } else if (entry.block !== block) this.write(root, entry.element, block, entry.block)
      const element = entry.element
      if (mode === 'edit' && listed) element.draggable = true
      else element.removeAttribute('draggable')
      if (block.type === 'p' && block.align) element.style.textAlign = block.align
      else if (element.hasAttribute('style')) element.removeAttribute('style')
      entry.block = block
      entry.mode = mode
      entry.listed = listed
      if (element.parentElement !== root || element.nextSibling !== anchor) target.insertBefore(element, anchor)
      anchor = element
    }
    if (fragment) root.insertBefore(fragment, end)
    this.committed = this.plan
    this.committedRoot = root
  }

  private write(root: HTMLElement, element: HTMLElement, block: Extract<Block, { type: 'p' | 'h' }>, before: Block | null) {
    const oldChildren = before && (before.type === 'p' || before.type === 'h') ? before.children : []
    const oldElements = before ? new Map(Array.from(element.children).map(child => [(child as HTMLElement).dataset.blockId, child])) : undefined
    for (const child of oldChildren) if (!block.children.some(next => next.id === child.id)) forgetLeaf(root, child.id, element)
    const wanted: ChildNode[] = block.children.map((child, index) => {
      const mapped = before ? findLeaf(root, child.id) : undefined
      const old = mapped?.nodes[0] ?? oldElements?.get(child.id)
      const end = index === block.children.length - 1
      if (old && oldChildren[index] === child && end === (index === oldChildren.length - 1)) return old
      const next = leaf(child, end)
      const result = old instanceof Element || old instanceof Text ? patch(old, next) : next
      if (child.type !== 'br') bindLeaf(root, child.id, result, element)
      return result
    })
    const last = block.children.at(-1)
    if (last && (last.type === 'text' || last.type === 'chars') && !(last.type === 'chars' && last.format) && plain(last).endsWith('\n')) wanted.push(document.createElement('br'))
    if (block.children.length === 0 || block.children.at(-1)?.type === 'br') wanted.push(document.createElement('br'))
    if (block.type === 'p' && block.children.at(-1)?.type === 'br') wanted.push(document.createElement('br'))
    let cursor = element.firstChild
    for (const child of wanted) {
      if (cursor === child) cursor = cursor.nextSibling
      else element.insertBefore(child, cursor)
    }
    while (cursor) {
      const next = cursor.nextSibling
      cursor.remove()
      cursor = next
    }
  }
}
